const GOAL_LABELS = {
  muscle: 'Lean Muscle',
  fat_loss: 'Fat-Loss',
  performance: 'Performance',
  wellbeing: 'Everyday Health',
};
const SESSION_LABELS = {
  '1_2': '1-2x / week',
  lt_3: '1-2x / week',
  '3_4': '3-4x / week, lifting',
  '5_plus': '5+x / week',
};
// Canonical tenure values (see assets/quiz-questions.json): lt_6m, 6m_24m, 2y_plus.
// Legacy keys (1_2y, 6m_2y) have been removed — they never appeared in the
// question schema and were dead branches that made the budget-cap mature-tenure
// check disagree with quiz-engine.js.
const TENURE_LABELS = {
  lt_6m: 'Just starting out',
  '6m_24m': 'Intermediate',
  '2y_plus': 'Advanced',
};
const BUDGET_LABELS = {
  lt_30: 'Under \u00a330',
  '30_60': 'Balanced',
  '60_100': 'Mid-range',
  '100_plus': 'Premium',
  best: 'Best available',
};

function formatMoney(pence) {
  return '\u00a3' + (pence / 100).toFixed(2);
}

export function renderResultsShell(answers, stack) {
  const goalLabel = GOAL_LABELS[answers.goal] || 'Your';
  const sessionLabel = SESSION_LABELS[answers.sessions_per_week] || '';
  const tenureLabel = TENURE_LABELS[answers.tenure] || '';
  const budgetLabel = BUDGET_LABELS[answers.budget] || '';
  const total = stack.items.reduce((s, i) => s + i.price * (i.quantity || 1), 0);
  const count = stack.items.length;
  const perDay = formatMoney(Math.round(total / 30));

  const pills = [
    `Goal: <strong>${goalLabel}</strong>`,
    sessionLabel,
    tenureLabel,
    budgetLabel ? `Budget: <strong>${budgetLabel}</strong>` : '',
    (answers.diet && answers.diet.length && !answers.diet.includes('none')) ? answers.diet.join(', ') : 'No dietary restrictions',
  ].filter(Boolean).map(t => `<span class="qz-why-pill">${t}</span>`).join('');

  const exclusionList = [
    ...(stack.exclusions || []).map(e =>
      `<div class="qz-excluded"><strong>Why we didn't recommend ${e.category.replace(/_/g, ' ')}:</strong> ${e.reason === 'no_candidates_matching_diet' ? 'no products matching your dietary requirements.' : e.reason === 'out_of_stock' ? 'currently out of stock.' : e.reason}</div>`
    ),
    ...(stack.dropped || []).map(d =>
      `<div class="qz-excluded"><strong>Why we removed ${d.category.replace(/_/g, ' ')}:</strong> over your budget. Swap a product to a lower tier or increase your budget.</div>`
    ),
  ].join('');

  // Social proof stubs from quiz-questions.json (loaded at window level).
  // `proof` is still used by the desktop reassurance strip below.
  const proof = window._quizQuestions?.social_proof_stubs?.[answers.goal];

  // CTA-adjacent social proof — reduced to a single Trustpilot-themed line.
  // Previously three rows (yellow-star stack avg, shoppers-this-month,
  // Trustpilot-green anchor) made the block visually busy and mixed two
  // star colours above the yellow CTA. Per product direction, collapsed
  // to one green-star Trustpilot anchor row.
  const ctaProofHtml = `
        <div class="qz-cta-proof" role="complementary" aria-label="Trustpilot rating">
          <span class="qz-cta-proof-tp" aria-hidden="true">\u2605\u2605\u2605\u2605\u2605</span>
          <span class="qz-cta-proof-txt"><strong>17,000+</strong> Trustpilot reviews &middot; Rated Excellent</span>
        </div>`;

  // Reassurance strip — 3 concise trust pills (dedupe with bottom panel)
  const reassurance = `
    <div class="qz-reassure">
      <div class="qz-reassure-item"><span class="tp-stars">\u2605\u2605\u2605\u2605\u2605</span> <strong>17,000+</strong> Trustpilot reviews</div>
      <div class="qz-reassure-item"><svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751A11.959 11.959 0 0 0 12 2.714Z"/></svg> UK retailer since 2005</div>
      ${proof ? `<div class="qz-reassure-item"><svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path d="m4.5 12.75 6 6 9-13.5"/></svg> <strong>${proof.count.toLocaleString()}</strong> ${goalLabel.toLowerCase()} stacks built this month</div>` : ''}
    </div>`;

  return `
    <div class="qz-kicker">Your personalised plan</div>
    <h1 class="qz-h1" tabindex="-1">Your <span class="qz-highlight">${goalLabel}</span> Stack</h1>
    <p class="qz-lead">All ${count} items are pre-selected. Untick anything you don't want &mdash; the total updates as you go.</p>
    ${reassurance}
    <div class="qz-why-row">${pills}</div>
    <div class="qz-result-grid">
      <div class="qz-stack-card">
        <div class="qz-stack-head">
          <p class="qz-stack-sub">Untick to remove &middot; Tap any item for full detail</p>
          <div class="qz-per-day">
            <div class="num">${perDay}</div>
            <div class="lab">per day</div>
          </div>
        </div>
        <div class="qz-stack-items" role="list"></div>
        <div class="qz-summary-bar">
          <div class="qz-summary-left"><strong class="qz-summary-count">${count} of ${count} items selected</strong></div>
          <div class="qz-summary-total">${formatMoney(total)}</div>
        </div>
        ${ctaProofHtml}
        <button class="qz-add-stack" type="button">Add ${count} items to bag</button>
        <div class="qz-ship-note"><strong>Free UK shipping</strong> on own-brand orders over \u00a345.</div>
        <div class="qz-social-proof">
          <h3 class="qz-sp-heading">Why this stack?</h3>
          <p class="qz-sp-summary">Built for your ${goalLabel.toLowerCase()} goal${sessionLabel ? ', ' + sessionLabel + ' training' : ''}${budgetLabel ? ' and ' + budgetLabel.toLowerCase() + ' budget' : ''}. Every product is in stock and ships same-day from our Hull warehouse.</p>
        </div>
        <div class="qz-restart-row"><button type="button" class="qz-restart">Start over</button></div>
      </div>
      <aside class="qz-aside">
        <div class="qz-aside-card">
          <p class="qz-aside-head">Also consider</p>
          <h3 class="qz-aside-title">Individual picks</h3>
          <div class="qz-also-consider"></div>
        </div>
      </aside>
    </div>
  `;
}

function renderStars(rating, count) {
  if (!count || !rating) return '';
  const full = Math.round(rating);
  return `<span class="qz-stars" aria-label="${rating} out of 5 stars">${'\u2605'.repeat(full)}${''.repeat(Math.max(0, 5 - full))}</span><span class="qz-review-count">${rating.toFixed(1)} (${count})</span>`;
}

export function renderItem(item, answers) {
  const compare = item.compareAt && item.compareAt > item.price
    ? `<span class="del">${formatMoney(item.compareAt)}</span>` : '';
  const imgSrc = item.image || (item.product && item.product.featuredImage) || '';
  const catSlug = (item.category || '').replace(/_/g, '');
  const catLabel = (item.category || '').replace(/_/g, ' ');
  const thumb = imgSrc
    ? `<img class="qz-img" src="${imgSrc}" alt="" loading="lazy" width="84" height="84">`
    : `<div class="qz-img ${catSlug}">${catLabel}</div>`;
  const reviewHtml = renderStars(item.rating, item.reviewCount);
  const uspHtml = (item.usps && item.usps.length)
    ? `<div class="qz-usps">${item.usps.map(u => `<span class="qz-usp">${u}</span>`).join('')}</div>`
    : '';

  const tiers = ['budget', 'trusted', 'premium'];
  // For each non-active tier, check whether cascading candidates would produce
  // a DIFFERENT handle than the current item. Only disable if EVERY candidate
  // equals the current handle (or there are no candidates) — otherwise the
  // swap handler can skip the same-handle candidate and pick a different one.
  // See bug: "Trusted, Premium, Budget fat burner doesnt change" — vegan users
  // only have one vegan fat_burner, so every tier click resolved to the same
  // handle with no visible change. And: some categories share a handle between
  // budget and trusted (e.g. xl-xtra-whey), which should NOT disable trusted
  // because trusted has other distinct candidates behind the shared one.
  const tierYieldsSame = (tier) => {
    if (tier === item.tier) return false;
    if (typeof window === 'undefined') return false;
    const products = window._quizProducts;
    const getCands = window.cascadedCandidates || window.resolveCandidates;
    if (!products || !getCands) return false;
    try {
      const cands = getCands(item.category, tier, answers.diet || ['none'], products, answers);
      if (!cands || !cands.length) return true; // no candidates at all
      // Disable only when there is NO distinct alternative.
      return cands.every(h => h === item.handle);
    } catch (_) {
      return false;
    }
  };
  const tierButtonHtml = (t) => {
    const isActive = t === item.tier;
    const disabled = !isActive && tierYieldsSame(t);
    const classes = ['qz-tier'];
    if (isActive) classes.push('active');
    if (disabled) classes.push('qz-tier-disabled');
    const attrs = disabled
      ? ' disabled aria-disabled="true" title="No alternative at this tier for your diet"'
      : '';
    return `<button type="button" class="${classes.join(' ')}" data-tier="${t}"${attrs}>${t[0].toUpperCase() + t.slice(1)}</button>`;
  };
  const tierPanel = `
        <div class="qz-swap-panel qz-swap-panel-inline">
          <p class="qz-swap-label">Switch tier:</p>
          ${tiers.map(tierButtonHtml).join('')}
        </div>`;

  return `
    <div class="qz-item" role="listitem"
         data-handle="${item.handle}" data-category="${item.category}" data-tier="${item.tier}">
      <button type="button" class="qz-check" aria-pressed="true" aria-label="Include ${item.title || 'item'} in stack">\u2713</button>
      ${thumb}
      <div class="qz-item-body">
        ${item.product?.vendor ? `<p class="qz-item-vendor">${item.product.vendor}</p>` : ''}
        <p class="qz-item-title">${item.title || item.handle || ''}</p>
        ${reviewHtml ? `<div class="qz-item-review">${reviewHtml}</div>` : ''}
        ${uspHtml}
      </div>
      <div class="qz-price-col">
        <div class="qz-price">${compare}${formatMoney(item.price)}</div>
      </div>
      ${tierPanel}
    </div>
  `;
}

export function attachHandlers(mount, stack, state) {
  // Deselect (idempotent — guard with data-bound)
  mount.querySelectorAll('.qz-item .qz-check').forEach(btn => {
    if (btn.dataset.bound === '1') return;
    btn.dataset.bound = '1';
    btn.addEventListener('click', () => {
      const row = btn.closest('.qz-item');
      const pressed = btn.getAttribute('aria-pressed') === 'true';
      btn.setAttribute('aria-pressed', String(!pressed));
      row.classList.toggle('deselected', pressed);
      recomputeSummary(mount, stack);
      window._quizTracking?.emit('quiz_item_deselected', {
        handle: row.dataset.handle, deselected: pressed,
      });
    });
  });
  // Details
  mount.querySelectorAll('.qz-details-btn').forEach(btn => {
    if (btn.dataset.bound === '1') return;
    btn.dataset.bound = '1';
    btn.addEventListener('click', () => {
      const panel = btn.nextElementSibling;
      const open = panel.hidden;
      panel.hidden = !open;
      btn.setAttribute('aria-expanded', String(open));
      btn.textContent = open ? 'Hide details' : 'Details';
    });
  });
  // Always-visible tier chooser — bind every inline swap panel
  mount.querySelectorAll('.qz-swap-panel-inline').forEach(panel => {
    if (panel.dataset.bound === '1') return;
    panel.dataset.bound = '1';
    const itemEl = panel.closest('.qz-item');
    bindSwapPanel(panel, itemEl, mount, stack, state);
  });
  // Add stack to Shopify cart (idempotent — guard with data-bound)
  const addBtn = mount.querySelector('.qz-add-stack');
  if (addBtn && addBtn.dataset.bound !== '1') {
    addBtn.dataset.bound = '1';
    addBtn.addEventListener('click', async () => {
      const activeItems = Array.from(mount.querySelectorAll('.qz-item:not(.deselected)'))
        .map(el => stack.items.find(i => i.handle === el.dataset.handle))
        .filter(Boolean);

      if (!activeItems.length) return;

      // ---------------------------------------------------------------
      // Shopify attribution (added audit-fixes-24):
      //
      // We stamp quiz metadata onto the cart in two places so it flows
      // onto every order in Shopify admin:
      //
      //   1. Line-item `properties` — per-line tier/goal/session.
      //      Underscore-prefixed keys are HIDDEN from customer-facing
      //      surfaces (cart page, checkout, email receipts, order
      //      status page) but visible in Order → line items in admin.
      //
      //   2. Cart `attributes` — order-level session/goal/budget/
      //      stack total/landing URL. Same underscore-prefix rule.
      //      Appears in Order → Additional details in admin, and is
      //      available to Shopify Flow triggers so we can auto-tag
      //      orders with `quiz`, `quiz-goal-muscle`, etc.
      //
      // A Shopify Flow workflow "Quiz Order Tagger" reads these
      // attributes on order creation and applies tags that are
      // filterable in the admin orders list and in native Shopify
      // Analytics (Sales by order tag).
      // ---------------------------------------------------------------
      const answers = state?.getAnswers?.() || {};
      const sessionId = state?.sessionId || '';
      const quizVersion = (typeof window !== 'undefined' && window.QUIZ_CONFIG?.version) || '1.0.0';
      const stackTotal = activeItems.reduce((s, i) => s + i.price * (i.quantity || 1), 0);
      const budget = Array.isArray(answers.budget) ? answers.budget.join(',') : (answers.budget || '');
      const goal = Array.isArray(answers.goal) ? answers.goal.join(',') : (answers.goal || '');
      const landingUrl = (typeof window !== 'undefined' && window.location) ? window.location.href : '';

      const resolved = await Promise.all(activeItems.map(async (it) => {
        try {
          const r = await fetch(`/products/${it.handle}.js`);
          const p = await r.json();
          if (!p || !p.variants || !p.variants.length) return null;
          return {
            id: p.variants[0].id,
            quantity: it.quantity || 1,
            properties: {
              _quiz_session: sessionId,
              _quiz_goal: goal,
              _quiz_tier: it.tier || '',
              _quiz_category: it.category || '',
              _quiz_version: quizVersion,
            },
          };
        } catch (err) {
          return null;
        }
      }));
      const items = resolved.filter(Boolean);
      if (!items.length) return;

      try {
        const cartResp = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!cartResp || !cartResp.ok) return;
      } catch (err) {
        return;
      }

      // Stamp order-level attributes. Fire-and-forget: if this fails we
      // still proceed — line-item properties alone are enough to attribute
      // the order, this just adds the nicer order-level summary.
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              _quiz_session_id: sessionId,
              _quiz_goal: goal,
              _quiz_budget: budget,
              _quiz_version: quizVersion,
              _quiz_stack_total: String(stackTotal),
              _quiz_landing_url: landingUrl,
            },
          }),
        });
      } catch (err) {
        // Non-fatal — order still has line-item properties.
      }

      window._quizTracking?.emit('quiz_add_stack', {
        stack_skus: activeItems.map(i => i.handle),
        stack_total: stackTotal,
      });

      // Refresh drawer contents, then open the slide cart (no full-page redirect).
      try {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        document.dispatchEvent(new CustomEvent('cart:open'));
      } catch (err) {
        // Fallback for environments without CustomEvent support.
        window.location.href = '/cart';
      }
    });
  }
  // Email capture was removed from the results UI per product direction.
  // The `submitQuizEmail` export remains available as a utility for any
  // future email-capture surface but no form is wired up on this page.
  // Start over — reset state and reload the page.
  // Must force scroll to top: browsers restore previous scroll position on
  // reload by default, which meant clicking "Start over" from the bottom of
  // a long results page left the user deep in the page with the freshly
  // rendered question 1 hidden above the fold.
  const restartBtn = mount.querySelector('.qz-restart');
  if (restartBtn && restartBtn.dataset.bound !== '1') {
    restartBtn.dataset.bound = '1';
    restartBtn.addEventListener('click', () => {
      state.reset();
      try {
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      } catch (_) { /* ignore */ }
      try { window.scrollTo(0, 0); } catch (_) { /* ignore */ }
      window.location.reload();
    });
  }
}

function bindSwapPanel(panel, itemEl, mount, stack, state) {
  panel.addEventListener('click', async (e) => {
    const btn = e.target.closest('.qz-tier');
    if (!btn || btn.classList.contains('active')) return;
    if (btn.disabled || btn.classList.contains('qz-tier-disabled')) return;
    const nextTier = btn.dataset.tier;
    const category = itemEl.dataset.category;
    const fromHandle = itemEl.dataset.handle;
    const answers = state.getAnswers();
    const getCands = window.cascadedCandidates || window.resolveCandidates;
    const candidates = getCands(
      category, nextTier, answers.diet || ['none'], window._quizProducts, answers
    );
    if (!candidates.length) return;
    // Try candidates in order; pick first available DIFFERENT product.
    // Skip same-handle candidates — some tiers share a handle with the current
    // product (e.g. xl-xtra-whey at both budget and trusted), and resolving to
    // the same handle would be a silent no-op from the user's perspective.
    const resolver = window.makeResolver({ fallback: {}, timeoutMs: 2000 });
    let replacement = null;
    for (const h of candidates) {
      if (h === fromHandle) continue;
      const p = await resolver(h).catch(() => null);
      if (p && p.available !== false) { replacement = p; break; }
    }
    if (!replacement) return;

    const idx = stack.items.findIndex(i => i.handle === fromHandle);
    if (idx < 0) return;
    const prodData = window._quizProducts || {};
    const swapReview = (prodData.reviews || {})[replacement.handle] || {};
    const swapUsps = (prodData.usps || {})[replacement.handle] || [];
    stack.items[idx] = {
      ...stack.items[idx],
      handle: replacement.handle,
      title: replacement.title,
      price: replacement.price,
      image: replacement.featuredImage,
      compareAt: replacement.compareAtPrice,
      product: replacement,
      tier: nextTier,
      rating: swapReview.rating || 0,
      reviewCount: swapReview.count || 0,
      usps: swapUsps,
    };

    // Re-run the budget cap to know if we are over.
    const capResult = window.applyBudgetCap(
      stack.items.map(({ category, tier, price, handle, quantity }) =>
        ({ category, tier, price, handle, quantity: quantity || 1 })),
      answers.budget,
      answers.goal,
      { tenureMature: ['6m_24m', '2y_plus'].includes(answers.tenure) }
    );
    const capBase = window.BUDGET_CAPS[answers.budget];
    const total = stack.items.reduce((s, i) => s + i.price * (i.quantity || 1), 0);
    const over = capBase !== Infinity && (total > capBase * 1.1 || capResult.overBudget);

    // Replace the DOM node with a freshly rendered item row.
    const newRow = document.createElement('div');
    newRow.innerHTML = renderItem(stack.items[idx], answers);
    itemEl.replaceWith(newRow.firstElementChild);
    // Re-bind handlers (guarded so already-bound nodes are skipped).
    attachHandlers(mount, stack, state);

    // Note: over-budget warning UI was removed per product direction —
    // we still track `over_budget` for analytics but no longer show a
    // scary red banner to the user.
    recomputeSummary(mount, stack);
    window._quizTracking?.emit('quiz_item_swapped', {
      from: fromHandle, to: replacement.handle, tier: nextTier, over_budget: over,
    });
  });
}

// Append an individual pick to the main stack. Called when the user taps the
// "+ Add to stack" button inside an "Also consider" card.
export function attachPickHandlers(mount, stack, state, answers, products) {
  mount.querySelectorAll('.qz-pick-card').forEach(card => {
    if (card.dataset.bound === '1') return;
    card.dataset.bound = '1';
    const btn = card.querySelector('.qz-pick-add');
    if (!btn) return;
    btn.addEventListener('click', async () => {
      if (btn.disabled) return;
      btn.disabled = true;
      const handle = card.dataset.handle;
      const category = card.dataset.category;
      // Guard against double-adds in case the same handle already exists.
      if (stack.items.some(i => i.handle === handle)) {
        collapseCard(card, 'Already in stack');
        return;
      }

      const resolver = window.makeResolver({ fallback: {}, timeoutMs: 2000 });
      const product = await resolver(handle).catch(() => null);
      if (!product || product.available === false) {
        btn.disabled = false;
        btn.textContent = 'Unavailable';
        return;
      }

      const review = (products.reviews || {})[handle] || {};
      const usps = (products.usps || {})[handle] || [];
      const newItem = {
        category,
        handle,
        tier: stack.tier || 'trusted',
        title: product.title,
        price: product.price,
        compareAt: product.compareAtPrice,
        image: product.featuredImage,
        product,
        quantity: 1,
        rating: review.rating || 0,
        reviewCount: review.count || 0,
        usps,
      };
      stack.items.push(newItem);

      // Insert the new row into the main stack list and rebind handlers.
      const stackList = mount.querySelector('.qz-stack-items');
      const row = document.createElement('div');
      row.innerHTML = renderItem(newItem, answers);
      stackList.appendChild(row.firstElementChild);
      // Bump the "X of X items selected" totals.
      const countEl = mount.querySelector('.qz-summary-count');
      if (countEl) {
        const active = mount.querySelectorAll('.qz-item:not(.deselected)').length;
        const total = mount.querySelectorAll('.qz-item').length;
        countEl.textContent = `${active} of ${total} item${total === 1 ? '' : 's'} selected`;
      }
      attachHandlers(mount, stack, state);
      recomputeSummary(mount, stack);

      // Note: over-budget warning UI was removed per product direction —
      // we still track `over_budget` for analytics but no longer show a
      // scary red banner to the user.

      collapseCard(card, 'Added to stack');
      window._quizTracking?.emit('quiz_pick_added', {
        handle, category, stack_size: stack.items.length,
      });
    });
  });
}

function collapseCard(card, label) {
  card.classList.add('qz-pick-added');
  const btn = card.querySelector('.qz-pick-add');
  if (btn) {
    btn.textContent = label;
    btn.disabled = true;
  }
}

export async function submitQuizEmail(email, tracking, state) {
  if (!email) return { ok: false };
  state?.setEmail?.(email);
  tracking?.setEmail?.(email);
  const answers = state?.getAnswers ? state.getAnswers() : {};

  if (tracking?.canTrack?.() && window.klaviyo?.identify) {
    try {
      await window.klaviyo.identify({ '$email': email });
      if (window.klaviyo.track) {
        await window.klaviyo.track('Quiz Plan Emailed', {
          quiz_session_id: state?.sessionId,
          goal: answers.goal,
        });
      }
    } catch (_) { /* swallow — server-side flow still runs */ }
  } else {
    // Pre-consent fallback: POST /contact with quiz-lead tag.
    const fd = new FormData();
    fd.set('form_type', 'contact');
    fd.set('utf8', '\u2713');
    fd.set('contact[email]', email);
    fd.set('contact[tags]', 'quiz-lead');
    fd.set('contact[body]', `Quiz goal: ${answers.goal}; session: ${state?.sessionId}`);
    try { await fetch('/contact#contact_form', { method: 'POST', body: fd }); }
    catch (_) { /* ignore — email still stored locally */ }
  }

  tracking?.emit?.('quiz_email_captured', { email_domain: email.split('@')[1] });
  return { ok: true };
}

export function recomputeSummary(mount, stack) {
  const activeEls = mount.querySelectorAll('.qz-item:not(.deselected)');
  const activeHandles = new Set(Array.from(activeEls).map(el => el.dataset.handle));
  const active = stack.items.filter(i => activeHandles.has(i.handle));
  const total = active.reduce((s, i) => s + i.price * (i.quantity || 1), 0);
  mount.querySelector('.qz-summary-count').textContent =
    `${active.length} of ${stack.items.length} item${stack.items.length === 1 ? '' : 's'} selected`;
  mount.querySelector('.qz-summary-total').textContent = formatMoney(total);
  mount.querySelector('.qz-add-stack').textContent =
    `Add ${active.length} item${active.length === 1 ? '' : 's'} to bag`;
  // Update per-day
  const perDayEl = mount.querySelector('.qz-per-day .num');
  if (perDayEl) perDayEl.textContent = formatMoney(Math.round(total / 30));
}

if (typeof window !== 'undefined') {
  window.renderItem = renderItem;
  window.attachHandlers = attachHandlers;
  window.attachPickHandlers = attachPickHandlers;
  window.renderResultsShell = renderResultsShell;
  window.recomputeSummary = recomputeSummary;
  window.submitQuizEmail = submitQuizEmail;
}

// Safe 2-item fallback stack — used when buildStack throws unexpectedly.
function safeStackFor(goal, products) {
  const cats = (products && products.categories) || {};
  const proteinHandle = cats.protein?.default?.trusted?.[0] || 'whey-protein';
  const creatineHandle = cats.creatine?.default?.budget?.[0] || 'creatine-monohydrate';
  return [
    {
      category: 'protein',
      handle: proteinHandle,
      title: 'Whey Protein',
      price: 4499,
      quantity: 1,
      tier: 'trusted',
      why: 'Safe fallback stack — contact support if you see this.',
    },
    {
      category: 'creatine',
      handle: creatineHandle,
      title: 'Creatine Monohydrate',
      price: 1499,
      quantity: 1,
      tier: 'budget',
      why: 'Safe fallback stack — contact support if you see this.',
    },
  ];
}

export async function handleQuizComplete(detail, root) {
  const { answers, state, products } = detail;
  window._quizProducts = products;
  // Store questions data for social proof stubs
  if (window._quizQuestions === undefined) {
    try {
      const cfg = window.QUIZ_CONFIG;
      if (cfg?.questionsUrl) {
        const resp = await fetch(cfg.questionsUrl);
        window._quizQuestions = await resp.json();
      }
    } catch (_) { window._quizQuestions = null; }
  }
  const resolver = window.makeResolver({ fallback: {}, timeoutMs: 2000 });
  let stack;
  try {
    stack = await window.buildStack(answers, products, resolver);
  } catch (err) {
    window._quizTracking?.emit('quiz_engine_fallback', { reason: err.message });
    stack = {
      items: safeStackFor(answers.goal, products),
      excluded: [],
      dropped: [],
      overBudget: false,
    };
  }
  // Hide intro elements (kicker, H1, lead) so only the results headings show
  root.querySelectorAll('.qz-kicker, .qz-h1, .qz-lead').forEach(el => { el.hidden = true; });

  const mount = root.querySelector('.qz-mount');
  mount.innerHTML = renderResultsShell(answers, stack);
  stack.items.forEach(i => {
    mount.querySelector('.qz-stack-items').insertAdjacentHTML('beforeend', renderItem(i, answers));
  });
  attachHandlers(mount, stack, state);

  // Render individual picks sidebar (parallel resolution)
  const alsoConsider = mount.querySelector('.qz-also-consider');
  if (alsoConsider && stack.individualPicks && stack.individualPicks.length) {
    const pickResolver = window.makeResolver({ fallback: {}, timeoutMs: 2000 });
    // Resolve all first candidates in parallel
    const resolved = await Promise.all(
      stack.individualPicks.map(async (category) => {
        // Cascade across tiers so an OOS budget-tier handle doesn't kill
        // the cross-sell — fall back to trusted/premium within the same
        // diet sub-map before giving up.
        const getCands = window.cascadedCandidates || window.resolveCandidates;
        const candidates = getCands(
          category, stack.tier, answers.diet || ['none'], products, answers
        );
        if (!candidates.length) return null;
        // Try each candidate in order, first available wins.
        for (const handle of candidates) {
          const product = await pickResolver(handle).catch(() => null);
          if (product && product.available) return { category, product };
        }
        return null;
      })
    );
    for (const entry of resolved) {
      if (!entry) continue;
      const { category, product } = entry;
      const review = (products.reviews || {})[product.handle] || {};
      const usps = (products.usps || {})[product.handle] || [];
      const price = '£' + (product.price / 100).toFixed(2);
      const imgSrc = product.featuredImage || '';
      const uspHtml = usps.length
        ? `<div class="qz-usps">${usps.map(u => '<span class="qz-usp">'+u+'</span>').join('')}</div>`
        : '';
      const reviewHtml = (review.count && review.rating)
        ? `<div class="qz-item-review"><span class="qz-stars" aria-label="${review.rating} out of 5 stars">${'\u2605'.repeat(Math.round(review.rating))}</span><span class="qz-review-count">${review.rating.toFixed ? review.rating.toFixed(1) : review.rating} (${review.count})</span></div>`
        : '';
      alsoConsider.insertAdjacentHTML('beforeend', `
        <div class="qz-pick-card" data-handle="${product.handle}" data-category="${category}">
          ${imgSrc ? '<img class="qz-pick-img" src="'+imgSrc+'" alt="" width="64" height="64" loading="lazy">' : ''}
          <div class="qz-pick-body">
            <p class="qz-pick-cat">${category.replace(/_/g, ' ')}</p>
            <p class="qz-pick-title">${product.title}</p>
            ${reviewHtml}
            ${uspHtml}
            <div class="qz-pick-footer">
              <p class="qz-pick-price">${price}</p>
              <button type="button" class="qz-pick-add" aria-label="Add ${product.title} to stack">+ Add to stack</button>
            </div>
          </div>
        </div>
      `);
    }
    if (!alsoConsider.children.length) {
      alsoConsider.closest('.qz-aside-card')?.style?.setProperty('display', 'none');
    }
    attachPickHandlers(mount, stack, state, answers, products);
  } else if (alsoConsider) {
    alsoConsider.closest('.qz-aside-card')?.style?.setProperty('display', 'none');
  }

  // Move focus to the page H1 for a11y. Previously targeted the
  // .qz-stack-title "Your core stack" h2 but that heading was
  // redundant with the page H1 "Your [Goal] Stack" above it and
  // was removed in audit-fixes-18.
  mount.querySelector('.qz-h1')?.focus();
  // Tracking
  window._quizTracking?.emit('quiz_completed', {
    branch: answers.goal,
    answers,
    stack_skus: stack.items.map(i => i.handle),
    stack_total: stack.items.reduce((s, i) => s + i.price * i.quantity, 0),
    replay_url: `${location.origin}/pages/quiz-page?r=${window.encodeReplay({ v: 1, sid: state.sessionId, answers })}`,
  });
}

// Main entry — listens for quiz:complete
(function bootstrap() {
  if (typeof document === 'undefined') return;
  const root = document.getElementById('quiz-root');
  if (!root) return;
  root.addEventListener('quiz:complete', (e) => handleQuizComplete(e.detail, root));
})();
